import { isOlderBrief } from '../utils/siteBriefFreshness';
import { subscribeBrief, reconciliationDelay, type BriefSnapshot } from '../realtime';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../AuthContext';
import {
  appendSiteBriefRequirement,
  stopSiteBriefTask,
  buildSiteBriefSiteStructure,
  createSession,
  deleteSiteBriefs,
  fetchSiteBriefById,
  fetchSiteBriefRequirementEvents,
  fetchSiteBriefRequirementsDictionary,
  fetchSiteBriefRenderedSite,
  fetchSiteBriefSiteStructure,
  fetchSiteBriefs,
  generateSiteBriefCore,
  gatherSiteBriefRequirements,
  fulfillSiteBriefVibeBlocks,
  isSiteBuilderConflictError,
  suggestSiteBriefRequirements,
  updateSiteBriefStatus
} from '../api';
import type {
  SiteBriefListItemType,
  SiteBriefListResponseType,
  SiteBriefListScopeType,
  RenderedSiteType,
  SiteBriefRegistryOptionsType,
  SiteBriefRequirementDefinitionType,
  SiteBriefRequirementEventType,
  SiteBriefRequirementInputType,
  SiteBriefRequirementType,
  SiteBriefRequirementStatusType,
  SiteBriefStatusType,
  SiteBriefType,
  SiteStructureType
} from '../api.types';
import { LANGUAGE_OPTIONS, type LanguageOption } from '../components/LanguageDropdown';
import BlockMenu from '../imports/BlockMenu';
import Background from '../imports/Background';
import SiteBriefDeleteDialog, { SiteBriefDeleteIcon } from '../components/SiteBriefDeleteDialog';
import { buildAgentChatMessageHref } from '../utils/agentChatDeepLink';

type FeedbackType = { type: 'success' | 'error'; message: string } | null;
type AlwaysIncludeFilterType = '' | 'yes' | 'no';
type RequirementFormType = {
  value: string;
  strength: string;
  origin: string;
  status: SiteBriefRequirementStatusType;
  confidence: string;
  scope: string;
  kind: string;
  evidenceSessionId: string;
  evidenceMessageId: string;
  evidenceQuote: string;
};

const STATUS_PRESENTATION: Record<SiteBriefStatusType, {
  label: string;
  description: string;
  accent: string;
  badge: string;
}> = {
  init: {
    label: 'Init',
    description: 'Initial prompts saved and ready for requirements gathering.',
    accent: 'border-t-[#64748b]',
    badge: 'bg-slate-100 text-slate-700'
  },
  gather_requirements: {
    label: 'Gather requirements',
    description: 'Briefs still collecting facts, constraints, and decisions.',
    accent: 'border-t-[#8b5cf6]',
    badge: 'bg-violet-100 text-violet-700'
  },
  suggest_requirements: {
    label: 'Suggest requirements',
    description: 'Briefs filling requirement gaps with explicit, reviewable assumptions.',
    accent: 'border-t-[#f59e0b]',
    badge: 'bg-amber-100 text-amber-700'
  },
  build_site_structure: {
    label: 'Build site structure',
    description: 'Pages and Vibe blocks planned, with a prompt for every block.',
    accent: 'border-t-[#0ea5e9]',
    badge: 'bg-sky-100 text-sky-700'
  },
  generate_site_core: {
    label: 'Generate site core',
    description: 'Website, pages, and empty Vibe blocks created.',
    accent: 'border-t-[#10b981]',
    badge: 'bg-emerald-100 text-emerald-700'
  },
  fulfill_vibe_blocks: {
    label: 'Fulfill Vibe blocks',
    description: 'Content generated and saved for every Vibe block.',
    accent: 'border-t-[#ec4899]',
    badge: 'bg-pink-100 text-pink-700'
  }
};

const STATUS_TRANSITION_TARGETS: Record<SiteBriefStatusType, SiteBriefStatusType[]> = {
  init: ['gather_requirements'],
  gather_requirements: ['suggest_requirements'],
  suggest_requirements: ['gather_requirements', 'build_site_structure'],
  build_site_structure: ['suggest_requirements', 'generate_site_core'],
  generate_site_core: ['fulfill_vibe_blocks'],
  fulfill_vibe_blocks: []
};

const inputClassName = 'w-full rounded-xl border border-[#dce3f0] bg-white px-3 py-2.5 text-sm text-[#212736] outline-none transition focus:border-[#165BFB] focus:ring-2 focus:ring-[#165BFB]/10';
const secondaryButtonClassName = 'rounded-xl border border-[#cfd9ec] bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] transition hover:border-[#9fb7e7] hover:bg-[#f8faff] disabled:cursor-not-allowed disabled:opacity-50';
const primaryButtonClassName = 'rounded-xl bg-[#165BFB] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#114edc] disabled:cursor-not-allowed disabled:opacity-50';

const LoadingSpinner = () => (
  <span
    className="inline-block size-4 flex-shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent"
    aria-hidden="true"
  />
);

const getErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error && error.message.trim() ? error.message : fallback
);

const formatLabel = (value: string): string => value
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[._-]+/g, ' ')
  .replace(/\b\w/g, character => character.toUpperCase());

const getRequirementSearchTerms = (value: string): string[] => {
  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) return [];
  if (!/[,;]/.test(normalizedValue)) return [normalizedValue];

  return [...new Set(
    normalizedValue
      .split(/[,;]/)
      .map(item => item.trim())
      .filter(Boolean)
  )];
};

const matchesRequirementSearch = (
  searchTerms: string[],
  values: Array<string | null | undefined>
): boolean => searchTerms.length === 0 || searchTerms.some(term => (
  values.some(value => value?.toLowerCase().includes(term))
));

const formatDate = (value: string): string => new Intl.DateTimeFormat('en', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
}).format(new Date(value));

const formatJson = (value: unknown): string => JSON.stringify(value, null, 2);

const getDefaultValueFromSchema = (schema: Record<string, unknown>): unknown => {
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];
  if ('default' in schema) return schema.default;
  if (schema.type === 'boolean') return false;
  if (schema.type === 'number' || schema.type === 'integer') {
    return typeof schema.minimum === 'number' ? schema.minimum : 0;
  }
  if (schema.type === 'array') return [];
  if (schema.type === 'object' || schema.properties) return {};
  return '';
};

type RequirementPillTone = 'gray' | 'blue' | 'green' | 'orange' | 'purple' | 'stone' | 'teal';

const MERGE_POLICY_TONES: Record<string, RequirementPillTone> = {
  replace: 'stone',
  'set-union': 'blue',
  'append-ordered': 'purple',
  'boolean-override': 'orange'
};

const RequirementPill = ({ children, tone = 'gray' }: {
  children: React.ReactNode;
  tone?: RequirementPillTone;
}) => {
  const className = {
    gray: 'bg-[#f1f4f9] text-[#647087]',
    blue: 'bg-[#eaf1ff] text-[#165BFB]',
    green: 'bg-emerald-50 text-emerald-700',
    orange: 'bg-amber-50 text-amber-700',
    purple: 'bg-violet-50 text-violet-700',
    stone: 'bg-stone-100 text-stone-700',
    teal: 'bg-teal-50 text-teal-700'
  }[tone];
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${className}`}>{children}</span>;
};

const FulfilledIndicator = () => (
  <span className="relative ml-2 inline-flex flex-shrink-0">
    <span
      className="peer inline-flex size-6 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200"
      title="Already fulfilled in the selected site brief"
      aria-hidden="true"
    >✓</span>
    <span
      className="pointer-events-none invisible absolute right-0 top-full z-30 mt-2 w-52 rounded-xl bg-[#273044] px-3 py-2 text-left text-[11px] font-semibold leading-4 text-white opacity-0 shadow-xl transition peer-hover:visible peer-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
      aria-hidden="true"
    >Already fulfilled in the selected site brief. Selecting this card shows its stored requirement below.</span>
    <span className="sr-only">Already fulfilled in the selected site brief.</span>
  </span>
);

const DetailField = ({ label, children, wide = false }: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) => (
  <div className={`rounded-2xl border border-[#e6eaf2] bg-[#fafbfe] p-4 ${wide ? 'md:col-span-2' : ''}`}>
    <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#9097a7]">{label}</div>
    <div className="break-words text-sm font-medium leading-6 text-[#333b4c]">{children}</div>
  </div>
);

type RequirementSourceTone = 'brief' | 'dictionary' | 'event';

const REQUIREMENT_SOURCE_STYLES: Record<RequirementSourceTone, {
  border: string;
  header: string;
  badge: string;
}> = {
  brief: {
    border: 'border-blue-200',
    header: 'border-blue-100 bg-gradient-to-r from-blue-50 to-white',
    badge: 'bg-[#165BFB] text-white'
  },
  dictionary: {
    border: 'border-violet-200',
    header: 'border-violet-100 bg-gradient-to-r from-violet-50 to-white',
    badge: 'bg-violet-600 text-white'
  },
  event: {
    border: 'border-amber-200',
    header: 'border-amber-100 bg-gradient-to-r from-amber-50 to-white',
    badge: 'bg-amber-500 text-white'
  }
};

const RequirementSourceSection = ({
  source,
  title,
  description,
  children,
  className = ''
}: {
  source: RequirementSourceTone;
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const styles = REQUIREMENT_SOURCE_STYLES[source];

  return (
    <section className={`overflow-hidden rounded-3xl border ${styles.border} ${className}`}>
      <div className={`border-b px-5 py-4 ${styles.header}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.11em] ${styles.badge}`}>
            {source === 'brief' ? 'Site brief model' : source === 'dictionary' ? 'Dictionary' : 'Event log'}
          </span>
          <h3 className="text-base font-bold text-[#2d3545]">{title}</h3>
        </div>
        <p className="mt-2 text-xs leading-5 text-[#717b8e]">{description}</p>
      </div>
      <div className="p-4 md:p-5">{children}</div>
    </section>
  );
};

const RequirementEvidence = ({ evidence }: {
  evidence: SiteBriefRequirementType['evidence'];
}) => (
  <div className="space-y-2">
    <div className="rounded-xl bg-white p-3 italic text-[#596277]">“{evidence.quote}”</div>
    <div className="break-all font-mono text-[11px] text-[#7c8597]">
      Session: {evidence.session_id}<br />Message: {evidence.message_id}
    </div>
    <a
      href={buildAgentChatMessageHref(evidence.session_id, evidence.message_id)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#7c8597] underline decoration-[#7c8597]/35 underline-offset-2 transition hover:decoration-[#7c8597] focus:outline-none focus:ring-2 focus:ring-[#7c8597]/25"
      title="Open this session and scroll to the source message in a new tab"
    >
      Open source message
      <svg
        className="size-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 5h5v5" />
        <path d="M10 14 19 5" />
        <path d="M19 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4" />
      </svg>
      <span className="sr-only"> in a new tab</span>
    </a>
  </div>
);

const HistorySubset = ({ events, selectedEventId, loading, onSelect }: {
  events: SiteBriefRequirementEventType[];
  selectedEventId: string | null;
  loading: boolean;
  onSelect: (eventId: string) => void;
}) => (
  <div className="ml-4 border-l-2 border-[#dce5f7] py-2 pl-3">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a93a6]">Change history</div>
    {loading ? (
      <div className="text-xs text-[#7b8291]">Loading changes…</div>
    ) : events.length === 0 ? (
      <div className="text-xs text-[#9aa1af]">No recorded changes.</div>
    ) : [...events].reverse().map((event, index) => (
      <button
        key={event.id}
        type="button"
        className={`mb-2 w-full rounded-xl border px-3 py-2 text-left transition ${selectedEventId === event.id
          ? 'border-[#165BFB] bg-[#eef4ff]'
          : 'border-[#e6eaf2] bg-white hover:border-[#bfd0f3]'}`}
        onClick={() => onSelect(event.id)}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-[#3d4658]">#{events.length - index} · {formatLabel(event.status)}</span>
          <span className="text-[10px] text-[#8d95a5]">{formatDate(event.time)}</span>
        </div>
        <div className="mt-1 truncate text-[11px] text-[#697286]">{event.id}</div>
      </button>
    ))}
  </div>
);

const SiteBriefsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = localStorage.getItem('authToken') || '';
  const [briefs, setBriefs] = useState<SiteBriefListItemType[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<SiteBriefListItemType | 'all' | null>(null);
  const [listReload, setListReload] = useState(0);
  const [briefScope, setBriefScope] = useState<SiteBriefListScopeType>('own');
  const [briefTotals, setBriefTotals] = useState<SiteBriefListResponseType['totals'] | null>(null);
  const briefScopeRef = useRef(briefScope);
  const [taskPollingError, setTaskPollingError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<SiteBriefRequirementDefinitionType[]>([]);
  const [registryOptions, setRegistryOptions] = useState<SiteBriefRegistryOptionsType | null>(null);
  const [selectedBriefId, setSelectedBriefId] = useState<string | null>(null);
  const [selectedBrief, setSelectedBriefState] = useState<SiteBriefType | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [events, setEvents] = useState<SiteBriefRequirementEventType[]>([]);
  const [search, setSearch] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [cardinalityFilter, setCardinalityFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [alwaysIncludeFilter, setAlwaysIncludeFilter] = useState<AlwaysIncludeFilterType>('');
  const [requirementKindFilter, setRequirementKindFilter] = useState('');
  const [requirementOriginFilter, setRequirementOriginFilter] = useState('');
  const [fulfilledCollapsed, setFulfilledCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [briefLoading, setBriefLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [suggestingRequirementsId, setSuggestingRequirementsId] = useState<string | null>(null);
  const [buildingStructureId, setBuildingStructureId] = useState<string | null>(null);
  const [renderingSiteId, setRenderingSiteId] = useState<string | null>(null);
  const [gatheringRequirementsId, setGatheringRequirementsId] = useState<string | null>(null);
  const [fulfillingBlocksId, setFulfillingBlocksId] = useState<string | null>(null);
  const [siteStructure, setSiteStructure] = useState<SiteStructureType | null>(null);
  const [siteStructureLoading, setSiteStructureLoading] = useState(false);
  const [renderedSite, setRenderedSite] = useState<RenderedSiteType | null>(null);
  const [renderedSiteLoading, setRenderedSiteLoading] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackType>(null);
  const [isEditingRequirement, setIsEditingRequirement] = useState(false);
  const [savingRequirement, setSavingRequirement] = useState(false);
  const [requirementForm, setRequirementForm] = useState<RequirementFormType | null>(null);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [emptyModalOpen, setEmptyModalOpen] = useState(false);
  const [siteBriefPrompt, setSiteBriefPrompt] = useState('');
  const [promptSessionName, setPromptSessionName] = useState('');
  const [promptSessionLanguage, setPromptSessionLanguage] = useState<LanguageOption>('en');
  const [emptySessionName, setEmptySessionName] = useState('');
  const [emptySessionLanguage, setEmptySessionLanguage] = useState<LanguageOption>('en');
  const [creationMode, setCreationMode] = useState<'prompt' | 'empty' | null>(null);
  const [creationError, setCreationError] = useState<string | null>(null);
  const statusMutationBriefIds = useRef(new Set<string>());
  const boardStatuses = registryOptions?.brief_statuses ?? [];
  const selectedBriefRef = useRef(selectedBrief);
  const snapshotRevision = useRef(0);
  const setSelectedBrief = useCallback((incoming: SiteBriefType | null) => {
    setSelectedBriefState(current => incoming && isOlderBrief(incoming, current) ? current : incoming);
  }, []);
  const selectedTaskPending = selectedBrief?.task?.state === 'queued' || (selectedBrief?.task?.state === 'running' || selectedBrief?.task?.state === 'cancelling');
  const hasPendingBriefTasks = briefs.some(brief => brief.task?.state === 'queued' || (brief.task?.state === 'running' || brief.task?.state === 'cancelling'))
    || selectedBrief?.task?.state === 'queued' || (selectedBrief?.task?.state === 'running' || selectedBrief?.task?.state === 'cancelling');

  const pendingTasksRef = useRef(hasPendingBriefTasks);
  pendingTasksRef.current = hasPendingBriefTasks;

  const applyBriefList = useCallback((result: SiteBriefListResponseType, revision = snapshotRevision.current): boolean => {
    if (result.scope !== briefScopeRef.current || revision !== snapshotRevision.current) return false;
    setBriefs(current => {
      const previous = new Map(current.map(brief => [brief.id, brief]));
      return result.site_briefs.map(brief => isOlderBrief(brief, previous.get(brief.id)) ? previous.get(brief.id)! : brief);
    });
    setBriefTotals(result.totals);
    return true;
  }, []);

  const toggleBriefScope = () => {
    const nextScope = briefScope === 'own' ? 'all' : 'own';
    briefScopeRef.current = nextScope;
    setBriefScope(nextScope);
    setLoading(true);
    setBriefs([]);
    setSelectedBriefId(null);
    setSelectedBrief(null);
    setTaskPollingError(null);
    setFeedback(null);
  };

  useEffect(() => { selectedBriefRef.current = selectedBrief; }, [selectedBrief]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let refreshAgain = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (cancelled) return;
      if (inFlight) { refreshAgain = true; return; }
      refreshAgain = false;
      inFlight = true;
      clearTimeout(timer);
      const revision = snapshotRevision.current;
      try {
        const result = await fetchSiteBriefs(token, briefScope);
        if (cancelled || revision !== snapshotRevision.current) return;
        const latest = result.site_briefs;
        const previous = selectedBriefRef.current?.id === selectedBriefId ? selectedBriefRef.current : null;
        const selected = latest.find(brief => brief.id === selectedBriefId);
        const changed = selected && (!previous || previous.version !== selected.version
          || JSON.stringify(previous.task) !== JSON.stringify(selected.task));
        const detail = changed && selectedBriefId ? await fetchSiteBriefById(token, selectedBriefId) : null;
        if (cancelled) return;
        if (!applyBriefList(result, revision)) return;
        if (detail) setSelectedBrief(detail);
        setTaskPollingError(null);
      } catch {
        if (!cancelled) setTaskPollingError('Could not refresh background progress.');
      } finally {
        inFlight = false;
        if (!cancelled) timer = setTimeout(poll, refreshAgain ? 0 : reconciliationDelay(pendingTasksRef.current ? 3000 : 7000));
      }
    };
    const onFocus = () => { void poll(); };
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribeBrief({ scope: briefScope }, onFocus, snapshot => {
      snapshotRevision.current += 1;
      if (snapshot.list) {
        applyBriefList(snapshot.list);
        setSelectedBriefId(current => current ?? snapshot.list?.site_briefs[0]?.id ?? null);
      }
      if (snapshot.detail?.site_brief.id === selectedBriefId) {
        setSelectedBrief(snapshot.detail.site_brief);
      }
      setTaskPollingError(null);
    });
    timer = setTimeout(poll, reconciliationDelay(3000));
    return () => { unsubscribe(); cancelled = true; clearTimeout(timer); window.removeEventListener('focus', onFocus); };
  }, [token, briefScope, selectedBriefId, applyBriefList, listReload, setSelectedBrief]);

  useEffect(() => {
    if ((!promptModalOpen && !emptyModalOpen) || creationMode !== null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPromptModalOpen(false);
        setEmptyModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [creationMode, emptyModalOpen, promptModalOpen]);

  useEffect(() => {
    let cancelled = false;
    fetchSiteBriefRequirementsDictionary(token)
      .then(dictionary => {
        if (cancelled) return;
        setDefinitions(dictionary.requirements);
        setRegistryOptions(dictionary.options);
      })
      .catch(error => {
        if (!cancelled) setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load the requirement registry.') });
      });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const revision = snapshotRevision.current;
    fetchSiteBriefs(token, briefScope)
      .then(result => {
        if (cancelled || !applyBriefList(result, revision)) return;
        setSelectedBriefId(current => result.site_briefs.some(brief => brief.id === current)
          ? current : result.site_briefs[0]?.id ?? null);
      })
      .catch(error => {
        if (!cancelled) setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load site briefs.') });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, briefScope, applyBriefList, listReload]);

  useEffect(() => {
    if (!selectedBriefId) {
      setSelectedBrief(null);
      setBriefLoading(false);
      setSelectedKey(null);
      setSelectedEventId(null);
      return;
    }
    let cancelled = false;
    setBriefLoading(true);
    setIsEditingRequirement(false);
    fetchSiteBriefById(token, selectedBriefId)
      .then(siteBrief => {
        if (cancelled) return;
        setSelectedBrief(siteBrief);
        setSelectedKey(currentKey => {
          const currentKeyExists = currentKey && siteBrief.requirements.some(item => item.key === currentKey);
          return currentKeyExists ? currentKey : siteBrief.requirements[0]?.key ?? null;
        });
        setSelectedEventId(currentEventId => {
          const currentEventExists = siteBrief.requirements.some(item => item.id === currentEventId);
          return currentEventExists ? currentEventId : siteBrief.requirements[0]?.id ?? null;
        });
      })
      .catch(error => {
        if (!cancelled) setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load the selected site brief.') });
      })
      .finally(() => {
        if (!cancelled) setBriefLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedBriefId, token, setSelectedBrief]);

  useEffect(() => {
    if (
      !selectedBrief
      || selectedBrief.id !== selectedBriefId
      || !['build_site_structure', 'generate_site_core', 'fulfill_vibe_blocks'].includes(selectedBrief.status)
    ) {
      setSiteStructure(null);
      setSiteStructureLoading(false);
      return;
    }

    let cancelled = false;
    setSiteStructureLoading(true);
    fetchSiteBriefSiteStructure(token, selectedBrief.id)
      .then(structure => {
        if (!cancelled) setSiteStructure(structure);
      })
      .catch(error => {
        if (cancelled) return;
        setSiteStructure(null);
        setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load the site structure.') });
      })
      .finally(() => {
        if (!cancelled) setSiteStructureLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedBrief, selectedBriefId, token]);

  useEffect(() => {
    if (
      !selectedBrief
      || selectedBrief.id !== selectedBriefId
      || (!['generate_site_core', 'fulfill_vibe_blocks'].includes(selectedBrief.status)
        && selectedBrief.task?.operation !== 'generate_site_core')
    ) {
      setRenderedSite(null);
      setRenderedSiteLoading(false);
      return;
    }

    let cancelled = false;
    setRenderedSiteLoading(true);
    fetchSiteBriefRenderedSite(token, selectedBrief.id)
      .then(site => {
        if (!cancelled) setRenderedSite(site);
      })
      .catch(error => {
        if (cancelled) return;
        if (selectedBrief.task?.state === 'queued' || (selectedBrief.task?.state === 'running' || selectedBrief.task?.state === 'cancelling')) return;
        setRenderedSite(null);
        setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load website creation details.') });
      })
      .finally(() => {
        if (!cancelled) setRenderedSiteLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedBrief, selectedBriefId, token]);

  useEffect(() => {
    const task = selectedBriefRef.current?.task;
    if (!selectedBriefId || !task
      || !['generate_site_core', 'fulfill_vibe_blocks', 'build_site'].includes(task.operation)
      || !selectedTaskPending) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let refreshAgain = false;
    const refreshProgress = async (snapshot?: BriefSnapshot) => {
      if (cancelled) return;
      if (inFlight) { refreshAgain = true; return; }
      inFlight = true;
      refreshAgain = false;
      clearTimeout(timer);
      try {
        const progress = await fetchSiteBriefRenderedSite(token, selectedBriefId);
        if (cancelled) return;
        setRenderedSite(progress);
        if (progress.fulfillment_status === 'completed') {
          const brief = snapshot?.detail?.site_brief.id === selectedBriefId
            ? snapshot.detail.site_brief : await fetchSiteBriefById(token, selectedBriefId);
          if (!cancelled) {
            setSelectedBrief(brief);
            setBriefs(current => current.map(item => item.id === brief.id && !isOlderBrief(brief, item) ? { ...item, ...brief } : item));
          }
          return;
        }
      } catch {
        // A transient progress-read failure must not interrupt the server-side generation.
      } finally { inFlight = false; }
      if (!cancelled) timer = setTimeout(() => { void refreshProgress(); }, refreshAgain ? 0 : reconciliationDelay(2000));
    };
    const unsubscribe = subscribeBrief({ scope: briefScope }, () => { void refreshProgress(); }, snapshot => {
      if (snapshot.detail?.site_brief.id === selectedBriefId) {
        void refreshProgress(snapshot);
      }
    });
    timer = setTimeout(() => { void refreshProgress(); }, 1000);
    return () => { unsubscribe(); cancelled = true; clearTimeout(timer); };
  }, [selectedBriefId, selectedTaskPending, token, briefScope, setSelectedBrief]);

  useEffect(() => {
    if (!selectedBriefId || !selectedKey) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    fetchSiteBriefRequirementEvents(token, selectedBriefId, selectedKey)
      .then(history => {
        if (cancelled) return;
        setEvents(history);
        setSelectedEventId(currentEventId => {
          if (currentEventId && history.some(event => event.id === currentEventId)) return currentEventId;
          const activeRequirement = selectedBrief?.requirements.find(item => item.key === selectedKey);
          return activeRequirement?.id ?? history.at(-1)?.id ?? null;
        });
      })
      .catch(error => {
        if (!cancelled) setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load requirement history.') });
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedBrief, selectedBriefId, selectedKey, token]);

  const selectedDefinition = definitions.find(definition => definition.key === selectedKey) ?? null;
  const selectedRequirement = (
    events.find(event => event.id === selectedEventId)
    ?? selectedBrief?.requirements.find(requirement => requirement.id === selectedEventId)
    ?? null
  );
  const definitionsByKey = useMemo(
    () => new Map(definitions.map(definition => [definition.key, definition])),
    [definitions]
  );
  const fulfilledKeys = new Set(selectedBrief?.requirements.map(requirement => requirement.key) ?? []);
  const selectedKeyIsFulfilled = selectedKey ? fulfilledKeys.has(selectedKey) : false;
  const selectedHistoryAnchorId = selectedBrief?.requirements.find(requirement => requirement.key === selectedKey)?.id;
  const requirementSearchTerms = useMemo(() => getRequirementSearchTerms(search), [search]);
  const hasSearch = requirementSearchTerms.length > 0;
  const activeFilterCount = [
    cardinalityFilter,
    scopeFilter,
    alwaysIncludeFilter,
    requirementKindFilter,
    requirementOriginFilter
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;
  const isFiltering = hasSearch || hasActiveFilters;
  const filteredFulfilledRequirements = useMemo(() => {
    const requirements = selectedBrief?.requirements ?? [];
    return requirements.filter(requirement => {
      const definition = definitionsByKey.get(requirement.key);
      const matchesSearch = matchesRequirementSearch(requirementSearchTerms, [
        requirement.key,
        formatLabel(requirement.key),
        definition?.description
      ]);
      const matchesAlwaysInclude = !alwaysIncludeFilter
        || definition?.always_include_in_prompt === (alwaysIncludeFilter === 'yes');

      return matchesSearch
        && (!cardinalityFilter || definition?.cardinality === cardinalityFilter)
        && (!scopeFilter || requirement.scope.kind === scopeFilter)
        && matchesAlwaysInclude
        && (!requirementKindFilter || requirement.kind === requirementKindFilter)
        && (!requirementOriginFilter || requirement.origin === requirementOriginFilter);
    });
  }, [
    alwaysIncludeFilter,
    cardinalityFilter,
    definitionsByKey,
    requirementKindFilter,
    requirementOriginFilter,
    requirementSearchTerms,
    scopeFilter,
    selectedBrief
  ]);
  const filteredDefinitions = useMemo(() => {
    return definitions.filter(definition => {
      const matchesSearch = matchesRequirementSearch(requirementSearchTerms, [
        definition.key,
        formatLabel(definition.key),
        definition.description
      ]);
      const matchesAlwaysInclude = !alwaysIncludeFilter
        || definition.always_include_in_prompt === (alwaysIncludeFilter === 'yes');

      return matchesSearch
        && (!cardinalityFilter || definition.cardinality === cardinalityFilter)
        && (!scopeFilter || definition.scopes.includes(scopeFilter))
        && matchesAlwaysInclude;
    });
  }, [alwaysIncludeFilter, cardinalityFilter, definitions, requirementSearchTerms, scopeFilter]);

  const clearFilters = () => {
    setCardinalityFilter('');
    setScopeFilter('');
    setAlwaysIncludeFilter('');
    setRequirementKindFilter('');
    setRequirementOriginFilter('');
  };

  const refreshBriefs = async (): Promise<SiteBriefListItemType[]> => {
    const revision = snapshotRevision.current;
    const result = await fetchSiteBriefs(token, briefScope);
    return applyBriefList(result, revision) ? result.site_briefs : [];
  };

  const recoverFromSiteBriefConflict = async (siteBriefId: string): Promise<SiteBriefType | null> => {
    const revision = snapshotRevision.current;
    const shouldRefreshSelectedBrief = selectedBriefId === siteBriefId;
    const [briefsResult, selectedBriefResult] = await Promise.allSettled([
      fetchSiteBriefs(token, briefScope),
      shouldRefreshSelectedBrief ? fetchSiteBriefById(token, siteBriefId) : Promise.resolve(null)
    ]);

    if (briefsResult.status === 'fulfilled') applyBriefList(briefsResult.value, revision);
    if (briefScopeRef.current !== briefScope) return null;
    if (selectedBriefResult.status !== 'fulfilled' || !selectedBriefResult.value) return null;

    const latestBrief = selectedBriefResult.value;
    setSelectedBrief(latestBrief);
    // The detail-dependent effects reload the selected requirement history, site structure,
    // and rendered-site state. The requirement form is deliberately kept for review/retry.
    return latestBrief;
  };

  const selectCreatedBrief = (
    nextBriefs: SiteBriefListItemType[],
    sessionId: string
  ): SiteBriefListItemType | undefined => {
    const createdBrief = nextBriefs.find(brief => (
      brief.session_id === sessionId || brief.sessions.some(session => session.id === sessionId)
    ));
    if (createdBrief) setSelectedBriefId(createdBrief.id);
    return createdBrief;
  };

  const handleCreateEmptyBrief = async () => {
    const sessionName = emptySessionName.trim();
    if (!sessionName) return;

    setCreationMode('empty');
    setFeedback(null);
    setCreationError(null);
    try {
      const session = await createSession(
        token,
        sessionName,
        [],
        emptySessionLanguage,
        { type: 'vibe_site', start_gathering: false }
      );
      const nextBriefs = await refreshBriefs();
      if (!selectCreatedBrief(nextBriefs, session.id)) {
        throw new Error('The new site brief was created but could not be found in the board.');
      }
      setEmptySessionName('');
      setEmptySessionLanguage('en');
      setEmptyModalOpen(false);
      setFeedback({ type: 'success', message: 'New empty site brief created.' });
    } catch (error) {
      setCreationError(getErrorMessage(error, 'Failed to create an empty site brief.'));
    } finally {
      setCreationMode(null);
    }
  };

  const handleCreateBriefFromPrompt = async () => {
    const prompt = siteBriefPrompt.trim();
    const sessionName = promptSessionName.trim();
    if (!prompt || !sessionName) return;

    setCreationMode('prompt');
    setFeedback(null);
    setCreationError(null);
    let createdSessionId: string | null = null;

    try {
      const session = await createSession(
        token,
        sessionName,
        [],
        promptSessionLanguage,
        { type: 'vibe_site', start_gathering: false, init_prompt: prompt }
      );
      createdSessionId = session.id;

      const nextBriefs = await refreshBriefs();
      if (!selectCreatedBrief(nextBriefs, createdSessionId)) {
        throw new Error('The new site brief was created but could not be found in the board.');
      }
      setPromptSessionName('');
      setPromptSessionLanguage('en');
      setSiteBriefPrompt('');
      setPromptModalOpen(false);
      setFeedback({ type: 'success', message: 'Initial prompt saved. Move the brief to Gather requirements to process it.' });
    } catch (error) {
      if (createdSessionId) {
        const nextBriefs = await refreshBriefs().catch(() => []);
        selectCreatedBrief(nextBriefs, createdSessionId);
        setPromptSessionName('');
        setPromptSessionLanguage('en');
        setSiteBriefPrompt('');
        setPromptModalOpen(false);
        setFeedback({
          type: 'error',
          message: getErrorMessage(error, 'The initial prompt was saved, but the board could not be refreshed.')
        });
      } else {
        setCreationError(getErrorMessage(error, 'Failed to create a site brief from the prompt.'));
      }
    } finally {
      setCreationMode(null);
    }
  };

  const handleStatusChange = async (siteBrief: SiteBriefListItemType, status: SiteBriefStatusType) => {
    if (siteBrief.task?.state === 'queued' || (siteBrief.task?.state === 'running' || siteBrief.task?.state === 'cancelling')) return;
    if (siteBrief.status === status && status !== 'fulfill_vibe_blocks') return;
    if (statusMutationBriefIds.current.has(siteBrief.id)) return;
    statusMutationBriefIds.current.add(siteBrief.id);
    const shouldGatherRequirements = siteBrief.status === 'init' && status === 'gather_requirements';
    const shouldSuggestRequirements = siteBrief.status === 'gather_requirements' && status === 'suggest_requirements';
    const shouldBuildStructure = siteBrief.status === 'suggest_requirements' && status === 'build_site_structure';
    const shouldRenderSite = siteBrief.status === 'build_site_structure' && status === 'generate_site_core';
    const shouldFulfillBlocks = ['generate_site_core', 'fulfill_vibe_blocks'].includes(siteBrief.status) && status === 'fulfill_vibe_blocks';
    setStatusUpdatingId(siteBrief.id);
    if (shouldGatherRequirements) setGatheringRequirementsId(siteBrief.id);
    if (shouldFulfillBlocks) setFulfillingBlocksId(siteBrief.id);
    if (shouldSuggestRequirements) setSuggestingRequirementsId(siteBrief.id);
    if (shouldBuildStructure) setBuildingStructureId(siteBrief.id);
    if (shouldRenderSite) setRenderingSiteId(siteBrief.id);
    setFeedback(null);
    try {
      let updated: SiteBriefType;
      if (shouldGatherRequirements) {
        updated = await gatherSiteBriefRequirements(token, siteBrief.id, siteBrief.version);
      } else if (shouldFulfillBlocks) {
        const result = await fulfillSiteBriefVibeBlocks(token, siteBrief.id, siteBrief.version);
        updated = result.siteBrief;
      } else if (shouldSuggestRequirements) {
        updated = await suggestSiteBriefRequirements(token, siteBrief.id, siteBrief.version);
      } else if (shouldBuildStructure) {
        const result = await buildSiteBriefSiteStructure(token, siteBrief.id, siteBrief.version);
        updated = result.siteBrief;
      } else if (shouldRenderSite) {
        const result = await generateSiteBriefCore(token, siteBrief.id, siteBrief.version);
        updated = result.siteBrief;
      } else {
        updated = await updateSiteBriefStatus(token, siteBrief.id, siteBrief.version, status);
      }
      if (selectedBriefId === siteBrief.id) setSelectedBrief(updated);
      await refreshBriefs();
      setFeedback({
        type: 'success',
        message: shouldGatherRequirements
          ? 'Requirements gathering queued. Progress will update automatically.'
          : shouldFulfillBlocks
            ? 'Vibe block generation queued. Progress will update automatically.'
            : shouldSuggestRequirements
          ? 'Requirements suggestion queued. Progress will update automatically.'
          : shouldBuildStructure
            ? 'Site structure generation queued. Progress will update automatically.'
            : shouldRenderSite
              ? 'Website core generation queued. Progress will update automatically.'
              : `Transition to ${STATUS_PRESENTATION[status].label} queued.`
      });
    } catch (error) {
      const conflict = isSiteBuilderConflictError(error);
      const latestBrief = conflict
        ? await recoverFromSiteBriefConflict(siteBrief.id)
        : null;
      setFeedback({
        type: 'error',
        message: `${getErrorMessage(
          error,
          shouldGatherRequirements ? 'Failed to gather initial requirements.'
            : shouldFulfillBlocks ? 'Vibe generation stopped. Retry to continue from the saved progress.'
              : shouldSuggestRequirements
            ? 'Failed to suggest requirements.'
            : shouldBuildStructure
              ? 'Failed to build the site structure.'
              : shouldRenderSite
                ? 'Failed to create the Weblium website.'
                : 'Failed to update brief status.'
        )}${conflict
          ? latestBrief
            ? ` Latest brief v${latestBrief.version} was loaded; review its current state before retrying.`
            : ' The brief changed elsewhere, but its latest details could not be loaded. Reload before retrying.'
          : ''}`
      });
      if (!conflict) await refreshBriefs().catch(() => undefined);
      if (!conflict && shouldFulfillBlocks && selectedBriefId === siteBrief.id) {
        await fetchSiteBriefRenderedSite(token, siteBrief.id).then(setRenderedSite).catch(() => undefined);
        await fetchSiteBriefById(token, siteBrief.id).then(setSelectedBrief).catch(() => undefined);
      }
    } finally {
      statusMutationBriefIds.current.delete(siteBrief.id);
      setStatusUpdatingId(null);
      if (shouldGatherRequirements) setGatheringRequirementsId(null);
      if (shouldFulfillBlocks) setFulfillingBlocksId(null);
      if (shouldSuggestRequirements) setSuggestingRequirementsId(null);
      if (shouldBuildStructure) setBuildingStructureId(null);
      if (shouldRenderSite) setRenderingSiteId(null);
    }
  };

  const refreshSiteStructure = async () => {
    if (!selectedBrief) return;
    setSiteStructureLoading(true);
    setFeedback(null);
    try {
      setSiteStructure(await fetchSiteBriefSiteStructure(token, selectedBrief.id));
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load the site structure.') });
    } finally {
      setSiteStructureLoading(false);
    }
  };

  const rebuildSiteStructure = async () => {
    if (!selectedBrief || selectedBrief.status !== 'build_site_structure' || selectedBrief.task?.state === 'queued' || (selectedBrief.task?.state === 'running' || selectedBrief.task?.state === 'cancelling')) return;
    setStatusUpdatingId(selectedBrief.id);
    setBuildingStructureId(selectedBrief.id);
    setFeedback(null);
    try {
      const result = await buildSiteBriefSiteStructure(token, selectedBrief.id, selectedBrief.version);
      setSelectedBrief(result.siteBrief);
      await refreshBriefs();
      setFeedback({ type: 'success', message: 'Site structure generation queued. Progress will update automatically.' });
    } catch (error) {
      const conflict = isSiteBuilderConflictError(error);
      const latestBrief = conflict
        ? await recoverFromSiteBriefConflict(selectedBrief.id)
        : null;
      setFeedback({
        type: 'error',
        message: `${getErrorMessage(error, 'Failed to rebuild the site structure.')}${conflict
          ? latestBrief
            ? ` Latest brief v${latestBrief.version} was loaded; review it before retrying.`
            : ' The brief changed elsewhere, but its latest details could not be loaded. Reload before retrying.'
          : ''}`
      });
      if (!conflict) await refreshBriefs().catch(() => undefined);
    } finally {
      setStatusUpdatingId(null);
      setBuildingStructureId(null);
    }
  };

  const selectRequirement = (key: string, eventId?: string) => {
    const activeRequirement = selectedBrief?.requirements.find(requirement => requirement.key === key);
    setSelectedKey(key);
    setSelectedEventId(eventId ?? activeRequirement?.id ?? null);
    setIsEditingRequirement(false);
  };

  const startRequirementEdit = () => {
    if (!selectedBrief || !selectedDefinition) return;
    const source = selectedRequirement;
    const firstSessionId = selectedBrief.sessions[0]?.id;
    setRequirementForm({
      value: formatJson(source?.value ?? getDefaultValueFromSchema(selectedDefinition.value_schema)),
      strength: source?.strength ?? registryOptions?.requirement_strengths[0] ?? 'soft',
      origin: source?.origin ?? 'system_suggest',
      status: source?.status ?? 'active',
      confidence: String(source?.confidence ?? 1),
      scope: formatJson(source?.scope ?? { kind: selectedDefinition.scopes[0] ?? 'site' }),
      kind: source?.kind ?? registryOptions?.requirement_kinds[0] ?? 'domain_fact',
      evidenceSessionId: source?.evidence.session_id ?? firstSessionId ?? crypto.randomUUID(),
      evidenceMessageId: source?.evidence.message_id ?? crypto.randomUUID(),
      evidenceQuote: source?.evidence.quote ?? 'Support engineer site brief edit'
    });
    setFeedback(null);
    setIsEditingRequirement(true);
  };

  const saveRequirement = async () => {
    if (!selectedBrief || !selectedDefinition || !requirementForm) return;
    let value: unknown;
    let scope: SiteBriefRequirementInputType['scope'];
    try {
      value = JSON.parse(requirementForm.value) as unknown;
      scope = JSON.parse(requirementForm.scope) as SiteBriefRequirementInputType['scope'];
    } catch {
      setFeedback({ type: 'error', message: 'Value and scope must both contain valid JSON.' });
      return;
    }

    setSavingRequirement(true);
    setFeedback(null);
    try {
      const result = await appendSiteBriefRequirement(token, selectedBrief.id, selectedBrief.version, {
        key: selectedDefinition.key,
        value,
        strength: requirementForm.strength,
        origin: requirementForm.origin,
        status: requirementForm.status,
        confidence: Number(requirementForm.confidence),
        scope,
        kind: requirementForm.kind,
        evidence: {
          session_id: requirementForm.evidenceSessionId,
          message_id: requirementForm.evidenceMessageId,
          quote: requirementForm.evidenceQuote
        },
        merge_policy: selectedDefinition.merge
      });
      setSelectedBrief(result.siteBrief);
      setSelectedEventId(result.requirementEvent.id);
      setEvents(await fetchSiteBriefRequirementEvents(token, selectedBrief.id, selectedDefinition.key));
      await refreshBriefs();
      setIsEditingRequirement(false);
      setFeedback({ type: 'success', message: `Requirement event ${result.requirementEvent.id} was appended.` });
    } catch (error) {
      const conflict = isSiteBuilderConflictError(error);
      const latestBrief = conflict
        ? await recoverFromSiteBriefConflict(selectedBrief.id)
        : null;
      setFeedback({
        type: 'error',
        message: `${getErrorMessage(error, 'Failed to save requirement.')}${conflict
          ? latestBrief
            ? ` Latest brief v${latestBrief.version} was loaded. Your unsaved requirement is still in the editor; review it and submit again.`
            : ' The brief changed elsewhere, but its latest details could not be loaded. Your unsaved requirement is still in the editor.'
          : ''}`
      });
    } finally {
      setSavingRequirement(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderHistory = () => (
    <HistorySubset
      events={events}
      selectedEventId={selectedEventId}
      loading={historyLoading}
      onSelect={setSelectedEventId}
    />
  );

  const renderFulfilledRequirementList = (requirements: SiteBriefType['requirements']) => (
    <div className="space-y-2">
      {requirements.map(requirement => (
        <div key={requirement.id}>
          <button
            type="button"
            className={`w-full rounded-2xl border p-3.5 text-left transition ${selectedEventId === requirement.id
              ? 'border-[#165BFB] bg-[#f1f5ff] shadow-[0_6px_18px_rgba(22,91,251,0.1)]'
              : 'border-[#dfe7f4] bg-white hover:border-[#b9cbed]'}`}
            onClick={() => selectRequirement(requirement.key, requirement.id)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><div className="truncate text-sm font-bold">{formatLabel(requirement.key)}</div><div className="mt-1 truncate text-[11px] text-[#7e8799]">{requirement.key}</div></div>
              <RequirementPill tone="green">Active</RequirementPill>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5"><RequirementPill>{formatLabel(requirement.kind)}</RequirementPill><RequirementPill>{Math.round(requirement.confidence * 100)}%</RequirementPill></div>
          </button>
          {requirement.id === selectedHistoryAnchorId && selectedKeyIsFulfilled && renderHistory()}
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative min-h-screen bg-[#f7f9fe] text-[#212736]">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden opacity-40">
        <div className="origin-top scale-90"><Background /></div>
      </div>
      <div className="relative z-10 min-h-screen">
        <header className="bg-white"><BlockMenu onLogout={handleLogout} showSessionControls={false} /></header>

        <main className="mx-auto w-full max-w-[1800px] px-4 py-7 md:px-7 md:py-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-7 flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
              <div className="max-w-[820px]">
                <div className="mb-3 inline-flex rounded-full bg-[#eaf1ff] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#165BFB]">AI Website Builder</div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Site briefs</h1>
                <p className="mt-3 text-base leading-7 text-[#676d7d]">Inspect every vibe-site brief, follow requirement history, and append validated changes from the shared registry.</p>
              </div>
              <div className="flex w-full flex-shrink-0 items-stretch gap-3 sm:w-auto">
                <div className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-[#dfe6f2] bg-white shadow-sm xl:min-w-[340px]">
                  <div className="grid grid-cols-2 divide-x divide-[#e8edf5] px-4 py-4" aria-live="polite">
                    <div className="flex flex-col items-start gap-2 pr-4 sm:flex-row sm:items-center sm:gap-3">
                      <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#edf3ff] px-1 text-lg font-bold tabular-nums text-[#165BFB]">{briefTotals?.own ?? '–'}</div>
                      <div><div className="whitespace-nowrap text-sm font-bold">Own briefs</div><div className="mt-0.5 text-xs text-[#858c9c]">Created by you</div></div>
                    </div>
                    <div className="flex flex-col items-start gap-2 pl-4 sm:flex-row sm:items-center sm:gap-3">
                      <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#f1f4f9] px-1 text-lg font-bold tabular-nums text-[#526079]">{briefTotals?.all ?? '–'}</div>
                      <div><div className="whitespace-nowrap text-sm font-bold">Total briefs</div><div className="mt-0.5 text-xs text-[#858c9c]">All owners</div></div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e8edf5] bg-[#f8faff] px-4 py-2.5">
                    <span className="text-xs text-[#737d91]">{briefScope === 'own' ? 'Showing your briefs' : 'Showing all briefs'}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={briefScope === 'all'}
                      aria-label="Show all briefs"
                      onClick={toggleBriefScope}
                      disabled={statusUpdatingId !== null || creationMode !== null || savingRequirement}
                      className="inline-flex items-center gap-2.5 rounded-full p-1 text-xs font-bold outline-none focus-visible:ring-2 focus-visible:ring-[#165BFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className={briefScope === 'own' ? 'text-[#165BFB]' : 'text-[#858c9c]'}>Own</span>
                      <span aria-hidden="true" className={`relative h-6 w-11 rounded-full transition-colors motion-reduce:transition-none ${briefScope === 'all' ? 'bg-[#165BFB]' : 'bg-[#cbd8ee]'}`}>
                        <span className={`absolute left-1 top-1 size-4 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${briefScope === 'all' ? 'translate-x-5' : 'translate-x-0'}`} />
                      </span>
                      <span className={briefScope === 'all' ? 'text-[#165BFB]' : 'text-[#858c9c]'}>All</span>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="brief-delete-all"
                  onClick={() => setDeleteTarget('all')}
                  title="Delete all your site briefs and Vibe Site sessions"
                  disabled={loading || statusUpdatingId !== null || creationMode !== null || savingRequirement}
                >
                  <span className="brief-delete-all-symbol"><SiteBriefDeleteIcon /></span>
                  <span>Delete all</span>
                  <span className="brief-delete-all-caption">Your briefs</span>
                </button>
              </div>
            </div>

            {feedback && (
              <div className={`mb-5 flex items-start justify-between gap-3 rounded-2xl px-4 py-3 text-sm ${feedback.type === 'success'
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border border-red-200 bg-red-50 text-red-800'}`}>
                <span>{feedback.message}</span>
                <button type="button" className="font-bold" onClick={() => setFeedback(null)} aria-label="Dismiss message">×</button>
              </div>
            )}

            <div className="grid items-start gap-6 lg:grid-cols-[minmax(340px,38%)_minmax(0,1fr)]">
              <aside className="overflow-hidden rounded-3xl border border-[#e1e7f2] bg-white shadow-[0_16px_45px_rgba(36,55,92,0.08)] lg:sticky lg:top-5 lg:flex lg:max-h-[calc(100vh-40px)] lg:flex-col">
                <div className="flex-shrink-0 border-b border-[#dce6fa] bg-gradient-to-br from-[#f3f7ff] via-white to-[#f7f4ff] p-5 md:p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#165BFB] text-lg text-white shadow-[0_8px_20px_rgba(22,91,251,0.22)]" aria-hidden="true">✦</div>
                    <div>
                      <h2 className="text-base font-bold">New site brief creations</h2>
                      <p className="mt-1 text-xs leading-5 text-[#737d91]">Start with a detailed idea or create a clean brief to fill manually.</p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                    <button
                      type="button"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#165BFB] px-3 py-2.5 text-xs font-bold leading-4 text-white shadow-sm transition hover:bg-[#114edc] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={creationMode !== null}
                      onClick={() => {
                        setFeedback(null);
                        setCreationError(null);
                        setPromptModalOpen(true);
                      }}
                    >
                      <span aria-hidden="true">✦</span>
                      New site brief from prompt
                    </button>
                    <button
                      type="button"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-[#b9caeb] bg-white px-3 py-2.5 text-xs font-bold leading-4 text-[#315486] shadow-sm transition hover:border-[#8eabe0] hover:bg-[#f8faff] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={creationMode !== null}
                      onClick={() => {
                        setFeedback(null);
                        setCreationError(null);
                        setEmptyModalOpen(true);
                      }}
                    >
                      <span className="text-base" aria-hidden="true">＋</span>
                      New empty site brief
                    </button>
                  </div>
                </div>

                <div className="flex-shrink-0 border-b border-[#edf0f5] p-5 md:p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div><h2 className="text-xl font-bold">Requirements</h2><p className="mt-1 text-sm text-[#7b8291]">{definitions.length} registry entries</p></div>
                    {selectedBrief && <RequirementPill tone="blue">Brief v{selectedBrief.version}</RequirementPill>}
                  </div>
                  <div className="relative mt-4">
                    <span className="pointer-events-none absolute left-3 top-2.5 text-[#9ca4b3]">⌕</span>
                    <input
                      className={`${inputClassName} pl-9`}
                      value={search}
                      onChange={event => {
                        const nextSearch = event.target.value;
                        setSearch(nextSearch);
                        if (nextSearch.trim()) setFulfilledCollapsed(true);
                      }}
                      placeholder="Search key or description; separate multiple with , or ;"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-xl border border-[#cfd9ec] bg-white px-3 py-2 text-xs font-bold text-[#4f5d74] transition hover:border-[#9fb7e7] hover:bg-[#f8faff]"
                      aria-controls="requirement-filters"
                      aria-expanded={filtersExpanded}
                      onClick={() => setFiltersExpanded(current => !current)}
                    >
                      <span aria-hidden="true">{filtersExpanded ? '▾' : '▸'}</span>
                      Filters
                      {hasActiveFilters && <RequirementPill tone="blue">{activeFilterCount}</RequirementPill>}
                    </button>
                    <button
                      type="button"
                      className="text-xs font-bold text-[#165BFB] transition hover:text-[#114edc] disabled:cursor-not-allowed disabled:text-[#a5adba]"
                      disabled={!hasActiveFilters}
                      onClick={clearFilters}
                    >Clear filters</button>
                  </div>
                  {filtersExpanded && (
                    <div id="requirement-filters" className="mt-3 rounded-2xl border border-[#e1e7f2] bg-[#f8faff] p-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#69758b]">Dictionary &amp; fulfilled</div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                        <label className="text-[11px] font-bold text-[#5d6679]">Cardinality
                          <select
                            className={`${inputClassName} mt-1 py-2 text-xs`}
                            value={cardinalityFilter}
                            onChange={event => {
                              setCardinalityFilter(event.target.value);
                              if (event.target.value) setFulfilledCollapsed(true);
                            }}
                          >
                            <option value="">All cardinalities</option>
                            {registryOptions?.requirement_cardinalities.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}
                          </select>
                        </label>
                        <label className="text-[11px] font-bold text-[#5d6679]">Scope
                          <select
                            className={`${inputClassName} mt-1 py-2 text-xs`}
                            value={scopeFilter}
                            onChange={event => {
                              setScopeFilter(event.target.value);
                              if (event.target.value) setFulfilledCollapsed(true);
                            }}
                          >
                            <option value="">All scopes</option>
                            {registryOptions?.scope_kinds.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}
                          </select>
                        </label>
                        <label className="text-[11px] font-bold text-[#5d6679] sm:col-span-2 lg:col-span-1 xl:col-span-2">Always include in prompt
                          <select
                            className={`${inputClassName} mt-1 py-2 text-xs`}
                            value={alwaysIncludeFilter}
                            onChange={event => {
                              const value = event.target.value as AlwaysIncludeFilterType;
                              setAlwaysIncludeFilter(value);
                              if (value) setFulfilledCollapsed(true);
                            }}
                          >
                            <option value="">Yes or no</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      </div>
                      <div className="mt-4 border-t border-[#e0e6f0] pt-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#69758b]">Fulfilled only</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                          <label className="text-[11px] font-bold text-[#5d6679]">Requirement kind
                            <select
                              className={`${inputClassName} mt-1 py-2 text-xs`}
                              value={requirementKindFilter}
                              onChange={event => {
                                setRequirementKindFilter(event.target.value);
                                if (event.target.value) setFulfilledCollapsed(true);
                              }}
                            >
                              <option value="">All kinds</option>
                              {registryOptions?.requirement_kinds.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}
                            </select>
                          </label>
                          <label className="text-[11px] font-bold text-[#5d6679]">Origin
                            <select
                              className={`${inputClassName} mt-1 py-2 text-xs`}
                              value={requirementOriginFilter}
                              onChange={event => {
                                setRequirementOriginFilter(event.target.value);
                                if (event.target.value) setFulfilledCollapsed(true);
                              }}
                            >
                              <option value="">All origins</option>
                              {registryOptions?.requirement_origins.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}
                            </select>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="max-h-[calc(100vh-230px)] overflow-y-auto p-4 md:p-5 lg:max-h-none lg:flex-1">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#697286]">Fulfilled in selected brief</h3>
                    <div className="flex items-center gap-2">
                      <RequirementPill tone="green">{selectedBrief?.requirements.length ?? 0}</RequirementPill>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border border-[#d9e2f1] bg-white px-2.5 py-1 text-[11px] font-bold text-[#5f6c82] transition hover:border-[#abc0e5] hover:text-[#165BFB] disabled:cursor-not-allowed disabled:bg-[#f5f7fa] disabled:text-[#9aa2b1]"
                        aria-controls="fulfilled-requirements"
                        aria-expanded={!fulfilledCollapsed}
                        disabled={isFiltering}
                        title={isFiltering ? 'Clear search and filters to expand fulfilled requirements' : undefined}
                        onClick={() => setFulfilledCollapsed(current => !current)}
                      >
                        <span aria-hidden="true">{fulfilledCollapsed ? '▸' : '▾'}</span>
                        {fulfilledCollapsed ? 'Expand' : 'Collapse'}
                      </button>
                    </div>
                  </div>
                  {isFiltering ? (
                    <div id="fulfilled-search-results" className="mb-7">
                      <div className="mb-2 flex items-center justify-between rounded-xl bg-[#f5f8ff] px-3 py-2">
                        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#64718a]">Filtered fulfilled</span>
                        <RequirementPill tone="blue">{filteredFulfilledRequirements.length}</RequirementPill>
                      </div>
                      {filteredFulfilledRequirements.length > 0
                        ? renderFulfilledRequirementList(filteredFulfilledRequirements)
                        : <div className="rounded-2xl border border-dashed border-[#d9dfeb] bg-[#fafbfe] p-4 text-center text-xs text-[#8a91a0]">No fulfilled requirements match the current search and filters.</div>}
                    </div>
                  ) : fulfilledCollapsed ? (
                    <div id="fulfilled-requirements" className="mb-7 rounded-2xl border border-dashed border-[#d9dfeb] bg-[#fafbfe] px-4 py-3 text-center text-xs text-[#8a91a0]">{selectedBrief?.requirements.length ?? 0} fulfilled requirements hidden.</div>
                  ) : !selectedBrief ? (
                    <div id="fulfilled-requirements" className="mb-6 rounded-2xl border border-dashed border-[#d9dfeb] bg-[#fafbfe] p-5 text-center text-sm text-[#8a91a0]">Select a brief to pin its fulfilled requirements here.</div>
                  ) : selectedBrief.requirements.length === 0 ? (
                    <div id="fulfilled-requirements" className="mb-6 rounded-2xl border border-dashed border-[#d9dfeb] bg-[#fafbfe] p-5 text-center text-sm text-[#8a91a0]">This brief has no active requirements yet.</div>
                  ) : (
                    <div id="fulfilled-requirements" className="mb-7">{renderFulfilledRequirementList(selectedBrief.requirements)}</div>
                  )}

                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#697286]">Full dictionary</h3>
                    <span className="text-xs text-[#9299a8]">{filteredDefinitions.length} shown</span>
                  </div>
                  <div className="space-y-2">
                    {filteredDefinitions.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-[#d9dfeb] bg-[#fafbfe] p-4 text-center text-xs text-[#8a91a0]">No dictionary entries match the current search and filters.</div>
                    ) : filteredDefinitions.map(definition => (
                      <div key={definition.key}>
                        <button
                          type="button"
                          className={`group w-full rounded-2xl border p-3.5 text-left transition ${selectedKey === definition.key
                            ? 'border-[#165BFB] bg-[#f3f7ff]'
                            : 'border-[#e6eaf2] bg-white hover:border-[#bfd0f3] hover:bg-[#fbfcff]'}`}
                          onClick={() => selectRequirement(definition.key)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0"><div className="truncate text-sm font-bold text-[#333b4c]">{formatLabel(definition.key)}</div><div className="mt-1 truncate text-[11px] text-[#7f8798]">{definition.key}</div></div>
                            {fulfilledKeys.has(definition.key) && <FulfilledIndicator />}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1.5"><RequirementPill>{formatLabel(definition.cardinality)}</RequirementPill><RequirementPill tone={MERGE_POLICY_TONES[definition.merge] ?? 'teal'}>{formatLabel(definition.merge)}</RequirementPill></div>
                        </button>
                        {selectedKey === definition.key && !selectedKeyIsFulfilled && renderHistory()}
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              <section className="min-w-0">
                {loading ? (
                  <div className="flex min-h-[500px] items-center justify-center rounded-3xl border border-[#e1e7f2] bg-white text-[#697286] shadow-sm">Loading site briefs…</div>
                ) : briefs.length === 0 ? (
                  <div className="flex min-h-[500px] flex-col items-center justify-center rounded-3xl border border-dashed border-[#d7deea] bg-white/80 px-8 text-center">
                    <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-[#edf3ff] text-3xl">◇</div>
                    <h2 className="text-xl font-bold">{briefScope === 'own' ? 'No briefs of your own yet' : 'No site briefs yet'}</h2>
                    <p className="mt-2 max-w-md text-sm leading-6 text-[#7b8291]">{briefScope === 'own'
                      ? 'Create your first site brief, or switch to All to explore briefs from everyone.'
                      : 'Create a vibe-site session to initialize its one-to-one brief and requirement event log.'}</p>
                  </div>
                ) : (
                  <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
                    {boardStatuses.map(status => {
                      const presentation = STATUS_PRESENTATION[status];
                      const items = briefs.filter(brief => brief.status === status);
                      return (
                        <div key={status} className={`min-w-0 rounded-3xl border border-t-4 border-[#e1e7f2] ${presentation.accent} bg-[#f9fbff]/95 p-4 shadow-[0_10px_32px_rgba(36,55,92,0.06)]`}>
                          <div className="mb-4 px-1">
                            <div className="flex items-center justify-between gap-2"><h2 className="text-base font-bold">{presentation.label}</h2><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${presentation.badge}`}>{items.length}</span></div>
                            <p className="mt-2 min-h-[40px] text-xs leading-5 text-[#7b8291]">{presentation.description}</p>
                          </div>
                          <div className="space-y-3">
                            {items.length === 0 ? (
                              <div className="rounded-2xl border border-dashed border-[#dce2ed] bg-white/60 px-4 py-8 text-center text-xs text-[#9aa1af]">No briefs in this stage</div>
                            ) : items.map(brief => {
                              const session = brief.sessions[0];
                              const isSelected = brief.id === selectedBriefId;
                              const availableStatuses = boardStatuses.filter(option => (
                                option === brief.status || STATUS_TRANSITION_TARGETS[brief.status].includes(option)
                              ));
                              return (
                                <article
                                  key={brief.id}
                                  className={`cursor-pointer rounded-2xl border bg-white p-4 transition ${isSelected
                                    ? 'border-[#165BFB] shadow-[0_10px_28px_rgba(22,91,251,0.14)] ring-2 ring-[#165BFB]/10'
                                    : 'border-[#e1e6ef] shadow-sm hover:-translate-y-0.5 hover:border-[#b9caed] hover:shadow-md'}`}
                                  onClick={() => setSelectedBriefId(brief.id)}
                                  onKeyDown={event => { if (event.key === 'Enter') setSelectedBriefId(brief.id); }}
                                  role="button"
                                  tabIndex={0}
                                  aria-busy={statusUpdatingId === brief.id}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0"><h3 className="truncate text-sm font-bold">{session?.name || 'Orphaned site brief'}</h3><p className="mt-1 truncate text-[11px] text-[#7e8798]">{session?.owner?.email || 'No linked owner'}</p></div>
                                    <div className="flex flex-shrink-0 items-center gap-2">
                                      <span className="brief-version-badge rounded-lg bg-[#eef3fb] text-[10px] font-bold text-[#53617a]">v{brief.version}</span>
                                      {brief.can_delete && (
                                        <button
                                          type="button"
                                          className="brief-delete-button"
                                          title="Delete site brief and session"
                                          aria-label={`Delete ${session?.name || 'site brief'} and its session`}
                                          disabled={statusUpdatingId === brief.id || savingRequirement}
                                          onClick={event => { event.stopPropagation(); setDeleteTarget(brief); }}
                                          onKeyDown={event => event.stopPropagation()}
                                        ><SiteBriefDeleteIcon /></button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="mt-4 rounded-xl bg-[#f7f9fd] p-3">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#969dac]">Generated Weblium website ID</div>
                                    <div className="mt-1 truncate font-mono text-[10px] text-[#596277]">{brief.weblium_website_id ?? 'Not generated yet'}</div>
                                  </div>
                                  <div className="mt-3 flex items-center justify-between text-xs"><span className="text-[#81899a]">Requirements</span><span className="font-bold">{brief.requirements_count}</span></div>
                                  <div className="mt-1 flex items-center justify-between text-xs"><span className="text-[#81899a]">Updated</span><span className="font-semibold text-[#596277]">{formatDate(brief.updated_at)}</span></div>
                                  {briefScope === 'own' && session && (
                                    <Link
                                      to={`/site-builder?session=${encodeURIComponent(session.id)}`}
                                      className="mt-3 inline-flex text-xs font-semibold text-[#165BFB] hover:underline"
                                      onClick={event => event.stopPropagation()}
                                      onKeyDown={event => event.stopPropagation()}
                                    >
                                      Open site conversation →
                                    </Link>
                                  )}
                                  <label className="mt-4 block text-[10px] font-bold uppercase tracking-[0.08em] text-[#8b93a4]">
                                    {availableStatuses.length > 1 ? 'Move to' : 'Status'}
                                    <select
                                      className={`${inputClassName} mt-1.5 py-2 text-xs`}
                                      value={brief.status}
                                      disabled={statusUpdatingId === brief.id || availableStatuses.length === 1 || brief.task?.state === 'queued' || (brief.task?.state === 'running' || brief.task?.state === 'cancelling')}
                                      onClick={event => event.stopPropagation()}
                                      onChange={event => handleStatusChange(brief, event.target.value as SiteBriefStatusType)}
                                    >
                                      {availableStatuses.map(option => <option key={option} value={option}>{STATUS_PRESENTATION[option].label}</option>)}
                                    </select>
                                  </label>
                                  {brief.task && (
                                    <div className={`mt-3 rounded-xl px-3 py-2 text-xs ${brief.task.state === 'failed'
                                      ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`} role="status" aria-live="polite">
                                      {formatLabel(brief.task.operation)}: {formatLabel(brief.task.state)}
                                      {brief.can_stop && ['queued', 'running'].includes(brief.task.state) && <button type="button" className={secondaryButtonClassName} onClick={async event => {
                                        event.stopPropagation();
                                        try { await stopSiteBriefTask(token, brief.id, brief.task!.id, brief.version); await refreshBriefs(); }
                                        catch (error) { setFeedback({ type: 'error', message: getErrorMessage(error, 'Could not stop task. Refresh and retry.') }); }
                                      }}>Stop</button>}
                                      {brief.task.error && <p className="mt-1">{brief.task.error}</p>}
                                    </div>
                                  )}
                                  {gatheringRequirementsId === brief.id && (
                                    <div className="mt-3 flex items-center gap-2 text-xs text-violet-700" role="status"><LoadingSpinner />Gathering initial requirements…</div>
                                  )}
                                  {fulfillingBlocksId === brief.id && (
                                    <div className="mt-3 flex items-center gap-2 text-xs text-pink-700" role="status"><LoadingSpinner />Generating Vibe blocks sequentially…</div>
                                  )}
                                  {suggestingRequirementsId === brief.id && (
                                    <div
                                      className="mt-3 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700"
                                      role="status"
                                    >
                                      <LoadingSpinner />
                                      Suggesting requirements…
                                    </div>
                                  )}
                                  {buildingStructureId === brief.id && (
                                    <div
                                      className="mt-3 flex items-center gap-2 rounded-xl bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700"
                                      role="status"
                                    >
                                      <LoadingSpinner />
                                      Building site structure…
                                    </div>
                                  )}
                                  {renderingSiteId === brief.id && (
                                    <div
                                      className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700"
                                      role="status"
                                    >
                                      <LoadingSpinner />
                                      Generating website core…
                                    </div>
                                  )}
                                </article>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            <section className="mt-6 overflow-hidden rounded-3xl border border-[#dfe6f2] bg-white shadow-[0_16px_45px_rgba(36,55,92,0.08)]">
                <div className="flex flex-col justify-between gap-4 border-b border-[#edf0f5] bg-gradient-to-r from-[#fbfcff] to-[#f2f6ff] p-5 md:flex-row md:items-center md:p-6">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-[#165BFB]">Requirement details</div>
                    <h2 className="mt-1 text-xl font-bold">{selectedDefinition ? formatLabel(selectedDefinition.key) : 'Select a requirement'}</h2>
                    {selectedDefinition && <p className="mt-1 font-mono text-xs text-[#758096]">{selectedDefinition.key}</p>}
                  </div>
                  {selectedBrief && selectedDefinition && !isEditingRequirement && (
                    <button type="button" className={primaryButtonClassName} onClick={startRequirementEdit}>{selectedRequirement ? 'Append edited version' : 'Add requirement'}</button>
                  )}
                </div>

                {!selectedBrief ? (
                  <div className="flex min-h-[250px] items-center justify-center px-6 text-center text-[#8a91a0]">Choose a site brief card to inspect its requirements.</div>
                ) : briefLoading ? (
                  <div className="flex min-h-[250px] items-center justify-center text-[#697286]">Loading brief details…</div>
                ) : !selectedDefinition ? (
                  <div className="flex min-h-[250px] items-center justify-center px-6 text-center text-[#8a91a0]">Choose a registry entry from the sidebar to see its contract and stored values.</div>
                ) : isEditingRequirement && requirementForm ? (
                  <div className="p-5 md:p-7">
                    <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-800">Saving appends a new immutable event. Replacement-style keys automatically disable active events with the same key and scope.</div>
                    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(300px,1fr)]">
                      <RequirementSourceSection
                        source="brief"
                        title="Requirement data"
                        description="These values are saved on the selected Site Brief requirement and may differ for every brief."
                      >
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="text-xs font-bold text-[#5d6679] md:col-span-2">Value (JSON)
                            <textarea className={`${inputClassName} mt-2 min-h-[150px] resize-y font-mono text-xs leading-5`} value={requirementForm.value} onChange={event => setRequirementForm({ ...requirementForm, value: event.target.value })} />
                          </label>
                          <label className="text-xs font-bold text-[#5d6679] md:col-span-2">Scope (JSON)
                            <textarea className={`${inputClassName} mt-2 min-h-[130px] resize-y font-mono text-xs leading-5`} value={requirementForm.scope} onChange={event => setRequirementForm({ ...requirementForm, scope: event.target.value })} />
                          </label>
                          <label className="text-xs font-bold text-[#5d6679]">Strength<select className={`${inputClassName} mt-2`} value={requirementForm.strength} onChange={event => setRequirementForm({ ...requirementForm, strength: event.target.value })}>{registryOptions?.requirement_strengths.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>
                          <label className="text-xs font-bold text-[#5d6679]">Origin<select className={`${inputClassName} mt-2`} value={requirementForm.origin} onChange={event => setRequirementForm({ ...requirementForm, origin: event.target.value })}>{registryOptions?.requirement_origins.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>
                          <label className="text-xs font-bold text-[#5d6679]">Status<select className={`${inputClassName} mt-2`} value={requirementForm.status} onChange={event => setRequirementForm({ ...requirementForm, status: event.target.value as SiteBriefRequirementStatusType })}>{registryOptions?.requirement_statuses.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>
                          <label className="text-xs font-bold text-[#5d6679]">Confidence<input className={`${inputClassName} mt-2`} type="number" min="0" max="1" step="0.01" value={requirementForm.confidence} onChange={event => setRequirementForm({ ...requirementForm, confidence: event.target.value })} /></label>
                          <label className="text-xs font-bold text-[#5d6679] md:col-span-2">Kind<select className={`${inputClassName} mt-2`} value={requirementForm.kind} onChange={event => setRequirementForm({ ...requirementForm, kind: event.target.value })}>{registryOptions?.requirement_kinds.map(option => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>
                          <label className="text-xs font-bold text-[#5d6679]">Evidence session UUID<input className={`${inputClassName} mt-2 font-mono text-xs`} value={requirementForm.evidenceSessionId} onChange={event => setRequirementForm({ ...requirementForm, evidenceSessionId: event.target.value })} /></label>
                          <label className="text-xs font-bold text-[#5d6679]">Evidence message UUID<input className={`${inputClassName} mt-2 font-mono text-xs`} value={requirementForm.evidenceMessageId} onChange={event => setRequirementForm({ ...requirementForm, evidenceMessageId: event.target.value })} /></label>
                          <label className="text-xs font-bold text-[#5d6679] md:col-span-2">Evidence quote<textarea className={`${inputClassName} mt-2 min-h-[90px] resize-y`} value={requirementForm.evidenceQuote} onChange={event => setRequirementForm({ ...requirementForm, evidenceQuote: event.target.value })} /></label>
                        </div>
                      </RequirementSourceSection>

                      <RequirementSourceSection
                        source="dictionary"
                        title="Read-only contract"
                        description="The selected key references this shared definition. These properties come from the dictionary and cannot be changed per brief."
                      >
                        <div className="grid gap-3">
                          <DetailField label="Dictionary key"><span className="font-mono text-xs">{selectedDefinition.key}</span></DetailField>
                          <DetailField label="Merge policy">{formatLabel(selectedDefinition.merge)}</DetailField>
                          <DetailField label="Cardinality">{formatLabel(selectedDefinition.cardinality)}</DetailField>
                          <DetailField label="Allowed scopes"><div className="flex flex-wrap gap-2">{selectedDefinition.scopes.map(scope => <RequirementPill key={scope}>{formatLabel(scope)}</RequirementPill>)}</div></DetailField>
                          <DetailField label="Value schema"><pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs leading-5">{formatJson(selectedDefinition.value_schema)}</pre></DetailField>
                        </div>
                      </RequirementSourceSection>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3"><button type="button" className={primaryButtonClassName} disabled={savingRequirement
                      || selectedBrief?.task?.state === 'queued' || (selectedBrief?.task?.state === 'running' || selectedBrief?.task?.state === 'cancelling')} onClick={saveRequirement}>{savingRequirement ? 'Saving…' : 'Append requirement event'}</button><button type="button" className={secondaryButtonClassName} disabled={savingRequirement} onClick={() => setIsEditingRequirement(false)}>Cancel</button></div>
                  </div>
                ) : (
                  <div className="p-5 md:p-7">
                    <div className="mb-5 flex flex-col justify-between gap-3 rounded-2xl border border-[#e3e8f2] bg-[#fafbfe] px-4 py-3 sm:flex-row sm:items-center">
                      <p className="text-xs leading-5 text-[#657084]">Field colors identify the source of every property. A brief value references its dictionary definition by key.</p>
                      <div className="flex flex-wrap gap-2">
                        <RequirementPill tone="blue">Site brief model</RequirementPill>
                        <RequirementPill tone="purple">Dictionary</RequirementPill>
                        {selectedRequirement && 'time' in selectedRequirement && <RequirementPill tone="orange">Event log</RequirementPill>}
                      </div>
                    </div>

                    <div className="grid items-start gap-5 xl:grid-cols-2">
                      <RequirementSourceSection
                        source="brief"
                        title="Requirement data"
                        description="These fields belong to this brief. Active values live on the Site Brief requirement; history keeps a snapshot of the same fields."
                      >
                        {selectedRequirement ? (
                          <div className="grid gap-4 md:grid-cols-2">
                            <DetailField label="Stored value" wide><pre className="max-h-56 overflow-auto whitespace-pre-wrap font-mono text-xs leading-5">{formatJson(selectedRequirement.value)}</pre></DetailField>
                            <DetailField label="Requirement ID"><span className="font-mono text-xs">{selectedRequirement.id}</span></DetailField>
                            <DetailField label="Status">{formatLabel(selectedRequirement.status)}</DetailField>
                            <DetailField label="Scope" wide><pre className="whitespace-pre-wrap font-mono text-xs">{formatJson(selectedRequirement.scope)}</pre></DetailField>
                            <DetailField label="Strength">{formatLabel(selectedRequirement.strength)}</DetailField>
                            <DetailField label="Kind">{formatLabel(selectedRequirement.kind)}</DetailField>
                            <DetailField label="Origin">{formatLabel(selectedRequirement.origin)}</DetailField>
                            <DetailField label="Confidence">{Math.round(selectedRequirement.confidence * 100)}%</DetailField>
                            <DetailField label="Evidence" wide><RequirementEvidence evidence={selectedRequirement.evidence} /></DetailField>
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/60 px-5 py-8 text-center">
                            <div className="text-sm font-bold text-blue-800">Not set in this brief</div>
                            <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-blue-700">This is currently only a dictionary definition. Add it to store a value, scope, confidence, evidence, and the other brief-specific fields.</p>
                          </div>
                        )}
                      </RequirementSourceSection>

                      <RequirementSourceSection
                        source="dictionary"
                        title="Requirement definition"
                        description="This shared contract defines how the referenced key is validated, merged, and used across every site brief."
                      >
                        <div className="grid gap-4 md:grid-cols-2">
                          <DetailField label="Dictionary key" wide><span className="font-mono text-xs">{selectedDefinition.key}</span></DetailField>
                          {selectedDefinition.description && <DetailField label="Description" wide>{selectedDefinition.description}</DetailField>}
                          <DetailField label="Cardinality">{formatLabel(selectedDefinition.cardinality)}</DetailField>
                          <DetailField label="Merge policy">{formatLabel(selectedDefinition.merge)}</DetailField>
                          <DetailField label="Conflict policy">{formatLabel(selectedDefinition.conflict)}</DetailField>
                          <DetailField label="Inference">{formatLabel(selectedDefinition.inference)}</DetailField>
                          <DetailField label="Always in prompt">{selectedDefinition.always_include_in_prompt ? 'Yes' : 'No'}</DetailField>
                          {selectedDefinition.validator_id && <DetailField label="Validator ID"><span className="font-mono text-xs">{selectedDefinition.validator_id}</span></DetailField>}
                          {selectedDefinition.capability_id && <DetailField label="Capability ID"><span className="font-mono text-xs">{selectedDefinition.capability_id}</span></DetailField>}
                          <DetailField label="Generation stages" wide><div className="flex flex-wrap gap-2">{selectedDefinition.affects.map(stage => <RequirementPill key={stage} tone="blue">{formatLabel(stage)}</RequirementPill>)}</div></DetailField>
                          <DetailField label="Allowed scopes" wide><div className="flex flex-wrap gap-2">{selectedDefinition.scopes.map(scope => <RequirementPill key={scope}>{formatLabel(scope)}</RequirementPill>)}</div></DetailField>
                          <DetailField label="Value schema" wide><pre className="max-h-72 overflow-auto whitespace-pre-wrap font-mono text-xs leading-5">{formatJson(selectedDefinition.value_schema)}</pre></DetailField>
                        </div>
                      </RequirementSourceSection>
                    </div>

                    {selectedRequirement && 'time' in selectedRequirement && (
                      <RequirementSourceSection
                        source="event"
                        title="Historical event metadata"
                        description="These fields belong only to the immutable event-log entry selected in the requirement history."
                        className="mt-5"
                      >
                        <div className="grid gap-4 md:grid-cols-2">
                          <DetailField label="Event time">{formatDate((selectedRequirement as SiteBriefRequirementEventType).time)}</DetailField>
                          <DetailField label="Merge policy snapshot">{formatLabel((selectedRequirement as SiteBriefRequirementEventType).merge_policy)}</DetailField>
                        </div>
                      </RequirementSourceSection>
                    )}
                  </div>
                )}
            </section>

            {selectedBrief && (
              <section className="mt-6 rounded-3xl border border-[#dfe6f2] bg-white p-6">
                <h2 className="text-lg font-bold">Initial prompt</h2>
                {taskPollingError && <p className="mt-2 text-sm text-amber-700" role="status">{taskPollingError}</p>}
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#596277]">{selectedBrief.init_prompt || 'No initial prompt was saved. Create a new brief from a prompt to start gathering requirements.'}</p>
                {selectedBrief.status === 'init' && (
                  <button type="button" className={`${primaryButtonClassName} mt-4`} disabled={!selectedBrief.init_prompt || statusUpdatingId === selectedBrief.id
                    || selectedBrief.task?.state === 'queued' || (selectedBrief.task?.state === 'running' || selectedBrief.task?.state === 'cancelling')}
                    onClick={() => { void handleStatusChange(selectedBrief, 'gather_requirements'); }}>
                    {gatheringRequirementsId === selectedBrief.id ? 'Gathering requirements…' : 'Gather requirements'}
                  </button>
                )}
              </section>
            )}

            {selectedBrief && (['generate_site_core', 'fulfill_vibe_blocks'].includes(selectedBrief.status)
              || selectedBrief.task?.operation === 'generate_site_core') && (
              <section className="mt-6 overflow-hidden rounded-3xl border border-emerald-200 bg-white shadow-[0_16px_45px_rgba(36,55,92,0.08)]">
                <div className="border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-white p-5 md:p-6">
                  <div className="text-xs font-bold uppercase tracking-[0.12em] text-emerald-700">Weblium website</div>
                  <h2 className="mt-1 text-xl font-bold">Website creation</h2>
                  <p className="mt-1 text-sm text-[#70798c]">{selectedBrief.status === 'generate_site_core'
                    ? 'The website core is ready. Generate content for its empty Vibe blocks in the next step.'
                    : selectedBrief.status === 'fulfill_vibe_blocks'
                      ? 'Vibe block content is saved. Continue editing the website in Weblium.'
                      : 'Website creation runs in the background. Saved progress appears below.'}</p>
                  {(selectedBrief.status === 'generate_site_core' || renderedSite?.fulfillment_status === 'failed') && (
                    <button type="button" className={`${primaryButtonClassName} mt-4`} disabled={statusUpdatingId === selectedBrief.id || selectedTaskPending}
                      onClick={() => { void handleStatusChange(selectedBrief, 'fulfill_vibe_blocks'); }}>
                      {fulfillingBlocksId === selectedBrief.id ? 'Fulfilling Vibe blocks…' : renderedSite?.fulfillment_status === 'failed' ? 'Retry Vibe generation' : 'Fulfill Vibe blocks'}
                    </button>
                  )}
                </div>
                {renderedSiteLoading && !renderedSite ? (
                  <div className="flex min-h-40 items-center justify-center gap-3 text-sm font-semibold text-emerald-700" role="status">
                    <LoadingSpinner />
                    Loading website creation details…
                  </div>
                ) : renderedSite ? (
                  <div className="p-5 md:p-7">
                    {renderedSite.fulfillment_error && <p role="alert" className="mb-4 text-sm text-red-700">{renderedSite.fulfillment_error}</p>}
                    <p className="mb-4 text-sm text-[#596277]">Vibe content: {formatLabel(renderedSite.fulfillment_status ?? 'pending')} · {renderedSite.fulfilled_block_keys?.length ?? 0}/{renderedSite.vibe_blocks?.length ?? 0} blocks saved</p>
                    {renderedSite.error && (
                      <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800">
                        {renderedSite.error.code}: {renderedSite.error.message}
                      </div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <DetailField label="Creation status">{formatLabel(renderedSite.creation_status)}</DetailField>
                      <DetailField label="Weblium website ID">
                        <span className="break-all font-mono text-xs">{renderedSite.weblium_website_id ?? 'Unavailable'}</span>
                      </DetailField>
                      <DetailField label="Requirements revision">{renderedSite.source_requirements_revision ?? 'Unavailable'}</DetailField>
                      <DetailField label="Completed">{renderedSite.completed_at ? formatDate(renderedSite.completed_at) : 'Unavailable'}</DetailField>
                    </div>
                    {(renderedSite.deferred_requirements?.length ?? 0) > 0 && (
                      <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <h3 className="text-sm font-bold text-amber-900">Deferred requirements</h3>
                        <p className="mt-1 text-xs leading-5 text-amber-800">These requests remain recorded and were not fulfilled by website creation.</p>
                        <div className="mt-3 space-y-2">
                          {renderedSite.deferred_requirements?.map(requirement => (
                            <div key={requirement.id} className="rounded-xl border border-amber-200 bg-white/70 px-3 py-2">
                              <div className="font-mono text-xs font-bold text-amber-900">{requirement.key}</div>
                              <div className="mt-1 text-xs leading-5 text-amber-800">{requirement.reason}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex min-h-40 items-center justify-center px-6 text-center text-sm text-[#8a91a0]">
                    Website creation details could not be loaded.
                  </div>
                )}
              </section>
            )}

            {selectedBrief && ['build_site_structure', 'generate_site_core', 'fulfill_vibe_blocks'].includes(selectedBrief.status) && (
              <section className="mt-6 overflow-hidden rounded-3xl border border-[#dfe6f2] bg-white shadow-[0_16px_45px_rgba(36,55,92,0.08)]">
                <div className="flex flex-col justify-between gap-4 border-b border-[#edf0f5] bg-gradient-to-r from-sky-50 to-white p-5 md:flex-row md:items-center md:p-6">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-sky-700">Weblium build blueprint</div>
                    <h2 className="mt-1 text-xl font-bold">Site structure</h2>
                    <p className="mt-1 text-sm text-[#70798c]">Website settings, UIKit, applications, pages, navigation, blocks, and feature setup.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={secondaryButtonClassName}
                      disabled={siteStructureLoading || buildingStructureId === selectedBrief.id}
                      onClick={() => { void refreshSiteStructure(); }}
                    >
                      {siteStructureLoading ? 'Refreshing…' : 'Refresh structure'}
                    </button>
                    {selectedBrief.status === 'build_site_structure' && !siteStructureLoading && (!siteStructure || siteStructure.is_stale) && (
                      <button
                        type="button"
                        className={primaryButtonClassName}
                        disabled={buildingStructureId === selectedBrief.id || selectedTaskPending}
                        onClick={() => { void rebuildSiteStructure(); }}
                      >
                        {buildingStructureId === selectedBrief.id ? (
                          <span className="inline-flex items-center gap-2"><LoadingSpinner />Rebuilding…</span>
                        ) : 'Rebuild from current requirements'}
                      </button>
                    )}
                    {selectedBrief.status === 'build_site_structure' && siteStructure && !siteStructure.is_stale && (
                      <button
                        type="button"
                        className={primaryButtonClassName}
                        disabled={renderingSiteId === selectedBrief.id || statusUpdatingId === selectedBrief.id || selectedTaskPending}
                        onClick={() => { void handleStatusChange(selectedBrief, 'generate_site_core'); }}
                      >
                        {renderingSiteId === selectedBrief.id ? (
                          <span className="inline-flex items-center gap-2"><LoadingSpinner />Generating core…</span>
                        ) : 'Generate site core'}
                      </button>
                    )}
                  </div>
                </div>

                {siteStructureLoading && !siteStructure ? (
                  <div className="flex min-h-52 items-center justify-center gap-3 text-sm font-semibold text-sky-700" role="status">
                    <LoadingSpinner />
                    Loading site structure…
                  </div>
                ) : siteStructure ? (
                  <div className="p-5 md:p-7">
                    {siteStructure.is_stale && (
                      <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                        {siteStructure.stale_reason ?? 'The saved site structure needs to be rebuilt.'}
                        {' '}Use Rebuild from current requirements before generating the website.
                      </div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
                      <DetailField label="Structure version">v{siteStructure.version}</DetailField>
                      <DetailField label="Source brief version">v{siteStructure.source_site_brief_version}</DetailField>
                      <DetailField label="Pages">{siteStructure.pages.length}</DetailField>
                      <DetailField label="Blocks">{
                        siteStructure.pages.reduce((count, page) => count + page.blocks.length, 0)
                        + siteStructure.global_blocks.length
                      }</DetailField>
                      <DetailField label="Applications">{siteStructure.applications.length}</DetailField>
                      <DetailField label="Updated">{formatDate(siteStructure.updated_at)}</DetailField>
                    </div>
                    <details className="mt-5 overflow-hidden rounded-2xl border border-[#dfe6f2] bg-[#f8faff]" open>
                      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-[#344054]">Complete structure JSON</summary>
                      <pre className="max-h-[680px] overflow-auto border-t border-[#e5eaf3] bg-[#111827] p-4 font-mono text-xs leading-5 text-[#dbeafe]">{formatJson(siteStructure)}</pre>
                    </details>
                  </div>
                ) : (
                  <div className="flex min-h-52 items-center justify-center px-6 text-center text-sm text-[#8a91a0]">
                    No site structure could be loaded for this brief.
                  </div>
                )}
              </section>
            )}
          </motion.div>
        </main>
      </div>

      {emptyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#15203b]/55 p-4 backdrop-blur-sm">
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_30px_90px_rgba(20,31,59,0.3)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="empty-site-brief-title"
          >
            <div className="flex items-start justify-between gap-5 border-b border-[#e9edf5] bg-gradient-to-r from-[#f5f8ff] to-[#fbf8ff] p-5 md:p-6">
              <div>
                <div className="mb-2 inline-flex rounded-full bg-[#e5eeff] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#165BFB]">Empty brief</div>
                <h2 id="empty-site-brief-title" className="text-xl font-bold">Create an empty site brief</h2>
                <p className="mt-2 text-sm leading-6 text-[#70798c]">Start a new vibe-site session with an empty requirements list.</p>
              </div>
              <button
                type="button"
                className="flex size-9 flex-shrink-0 items-center justify-center rounded-xl border border-[#dce3ef] bg-white text-xl text-[#6c7587] transition hover:border-[#bac8df] hover:text-[#26334b] disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close empty site brief dialog"
                disabled={creationMode === 'empty'}
                onClick={() => setEmptyModalOpen(false)}
              >×</button>
            </div>
            <form
              className="p-5 md:p-6"
              onSubmit={event => {
                event.preventDefault();
                void handleCreateEmptyBrief();
              }}
            >
              <label className="text-sm font-bold text-[#4d586d]" htmlFor="empty-site-brief-session-name">Session name</label>
              <input
                id="empty-site-brief-session-name"
                type="text"
                className={`${inputClassName} mt-2`}
                value={emptySessionName}
                placeholder="Enter session name"
                disabled={creationMode === 'empty'}
                autoFocus
                onChange={event => setEmptySessionName(event.target.value)}
              />
              <label className="mt-5 block text-sm font-bold text-[#4d586d]" htmlFor="empty-site-brief-language">Language</label>
              <select
                id="empty-site-brief-language"
                className={`${inputClassName} mt-2`}
                value={emptySessionLanguage}
                disabled={creationMode === 'empty'}
                onChange={event => setEmptySessionLanguage(event.target.value as LanguageOption)}
              >
                {LANGUAGE_OPTIONS.map(language => <option key={language} value={language}>{language}</option>)}
              </select>
              {creationError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" role="alert">
                  {creationError}
                </div>
              )}
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={creationMode === 'empty'}
                  onClick={() => setEmptyModalOpen(false)}
                >Cancel</button>
                <button
                  type="submit"
                  className={`${primaryButtonClassName} inline-flex min-w-36 items-center justify-center gap-2`}
                  disabled={
                    creationMode === 'empty'
                    || emptySessionName.trim().length === 0
                  }
                >
                  {creationMode === 'empty' && <LoadingSpinner />}
                  {creationMode === 'empty' ? 'Creating…' : 'Create empty brief'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {promptModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#15203b]/55 p-4 backdrop-blur-sm">
          <div
            className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_30px_90px_rgba(20,31,59,0.3)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="site-brief-prompt-title"
          >
            <div className="flex items-start justify-between gap-5 border-b border-[#e9edf5] bg-gradient-to-r from-[#f5f8ff] to-[#fbf8ff] p-5 md:p-7">
              <div>
                <div className="mb-2 inline-flex rounded-full bg-[#e5eeff] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#165BFB]">Prompt creation</div>
                <h2 id="site-brief-prompt-title" className="text-xl font-bold md:text-2xl">Create a site brief from a prompt</h2>
                <p className="mt-2 max-w-xl text-sm leading-6 text-[#70798c]">Describe the business, audience, pages, features, visual preferences, and constraints. Your prompt is saved immediately; requirements are gathered when you advance the brief.</p>
              </div>
              <button
                type="button"
                className="flex size-9 flex-shrink-0 items-center justify-center rounded-xl border border-[#dce3ef] bg-white text-xl text-[#6c7587] transition hover:border-[#bac8df] hover:text-[#26334b] disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close prompt dialog"
                disabled={creationMode === 'prompt'}
                onClick={() => setPromptModalOpen(false)}
              >×</button>
            </div>
            <form
              className="p-5 md:p-7"
              onSubmit={event => {
                event.preventDefault();
                void handleCreateBriefFromPrompt();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_150px]">
                <div>
                  <label className="text-sm font-bold text-[#4d586d]" htmlFor="prompt-site-brief-session-name">Session name</label>
                  <input
                    id="prompt-site-brief-session-name"
                    type="text"
                    className={`${inputClassName} mt-2`}
                    value={promptSessionName}
                    placeholder="Enter session name"
                    disabled={creationMode === 'prompt'}
                    autoFocus
                    onChange={event => setPromptSessionName(event.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-[#4d586d]" htmlFor="prompt-site-brief-language">Language</label>
                  <select
                    id="prompt-site-brief-language"
                    className={`${inputClassName} mt-2`}
                    value={promptSessionLanguage}
                    disabled={creationMode === 'prompt'}
                    onChange={event => setPromptSessionLanguage(event.target.value as LanguageOption)}
                  >
                    {LANGUAGE_OPTIONS.map(language => <option key={language} value={language}>{language}</option>)}
                  </select>
                </div>
              </div>
              <label className="mt-5 block text-sm font-bold text-[#4d586d]" htmlFor="site-brief-prompt">Site brief prompt</label>
              <textarea
                id="site-brief-prompt"
                className={`${inputClassName} mt-2 min-h-[220px] resize-y px-4 py-3 text-sm leading-6`}
                value={siteBriefPrompt}
                rows={9}
                placeholder="Example: We are launching a boutique ceramics studio for design-conscious homeowners. The site needs a warm editorial feel, an online shop, workshop booking, an about page, and bilingual English/French content…"
                disabled={creationMode === 'prompt'}
                onChange={event => setSiteBriefPrompt(event.target.value)}
              />
              {creationError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" role="alert">
                  {creationError}
                </div>
              )}
              <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={creationMode === 'prompt'}
                  onClick={() => setPromptModalOpen(false)}
                >Cancel</button>
                <button
                  type="submit"
                  className={`${primaryButtonClassName} inline-flex min-w-44 items-center justify-center gap-2`}
                  disabled={
                    creationMode === 'prompt'
                    || promptSessionName.trim().length === 0
                    || siteBriefPrompt.trim().length === 0
                  }
                >
                  {creationMode === 'prompt' && <LoadingSpinner />}
                  {creationMode === 'prompt' ? 'Creating from prompt…' : 'Create from prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteTarget && (
        <SiteBriefDeleteDialog
          all={deleteTarget === 'all'}
          name={deleteTarget === 'all' ? undefined : deleteTarget.sessions[0]?.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            const result = await deleteSiteBriefs(token, deleteTarget === 'all' ? undefined : deleteTarget);
            const removedIds = new Set(briefs.filter(brief => deleteTarget === 'all' ? brief.can_delete : brief.id === deleteTarget.id).map(brief => brief.id));
            setBriefs(current => current.filter(brief => !removedIds.has(brief.id)));
            if (selectedBriefId && removedIds.has(selectedBriefId)) {
              setSelectedBriefId(null);
              setSelectedBrief(null);
              setSelectedKey(null);
              setSelectedEventId(null);
              setEvents([]);
              setSiteStructure(null);
              setRenderedSite(null);
              setRequirementForm(null);
              setIsEditingRequirement(false);
            }
            setFeedback({ type: 'success', message: `Deleted ${result.deleted_briefs} site brief(s) and ${result.deleted_sessions} Vibe Site session(s).` });
            setListReload(current => current + 1);
          }}
        />
      )}
    </div>
  );
};

export default SiteBriefsPage;
